import { Box, Typography } from '@mui/material';
import { MultiSelectQuestion } from 'src/suitability/engine/components/multi-select-question';
import { PercentQuestion } from 'src/suitability/engine/components/percent-question';
import { RadioQuestion } from 'src/suitability/engine/components/radio-question';
import type { TestStepProps } from 'src/suitability/engine/types';
import {
  S2_MAX_PERCENTAGE,
  S2_MIN_PERCENTAGE,
  type ConsilioAnswers,
  type ConsilioSustainabilityAnswers,
} from './answers';
import {
  toMultiSelectOptions,
  toRadioOptions,
  useConsilioContent,
} from './content';
import {
  isSelfDetermined,
  opensAdverseImpact,
  opensSfdr,
  opensTaxonomy,
  pruneSustainability,
} from './sustainability';

const toOrders = (selection: Record<string, boolean>): number[] =>
  Object.entries(selection)
    .filter(([, isSelected]) => isSelected)
    .map(([order]) => Number(order))
    .sort((a, b) => a - b);

const toSelection = (orders: number[] | undefined): Record<string, boolean> =>
  Object.fromEntries((orders ?? []).map((order) => [String(order), true]));

export const StepSustainability = ({
  answers,
  onUpdate,
}: TestStepProps<ConsilioAnswers>) => {
  const content = useConsilioContent();
  const step = content.step('sustainability');
  const value = answers.sustainability;

  const update = (patch: Partial<ConsilioSustainabilityAnswers>) =>
    onUpdate({ sustainability: pruneSustainability({ ...value, ...patch }) });

  const single = (key: 's1' | 's4' | 's5') => {
    const question = content.question(key);
    const answer = value?.[key];

    return (
      <RadioQuestion
        question={question.prompt}
        options={toRadioOptions(key, question)}
        value={answer === undefined ? undefined : String(answer)}
        onChange={(order) => update({ [key]: Number(order) })}
        required={key !== 's1'}
      />
    );
  };

  const multiple = (key: 's3' | 's6') => {
    const question = content.question(key);

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="textMd" sx={{ fontWeight: 600, mb: 1.5 }}>
          {question.prompt}
          <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
            *
          </Box>
        </Typography>
        <MultiSelectQuestion
          options={toMultiSelectOptions(question)}
          value={toSelection(value?.[key])}
          onChange={(selection) => update({ [key]: toOrders(selection) })}
        />
      </Box>
    );
  };

  const percentage = content.question('s2');

  return (
    <Box>
      <Typography variant="textXl" sx={{ fontWeight: 700, mb: 1 }}>
        {step.title}
      </Typography>
      {step.intro && (
        <Typography
          variant="textSm"
          sx={{ color: 'text.secondary', mb: 3, display: 'block' }}
        >
          {step.intro}
        </Typography>
      )}

      {single('s1')}

      {isSelfDetermined(value) && (
        <>
          <PercentQuestion
            question={percentage.prompt}
            suffix={percentage.suffix}
            value={value?.s2}
            onChange={(s2) => update({ s2 })}
            min={S2_MIN_PERCENTAGE}
            max={S2_MAX_PERCENTAGE}
            required
          />
          {multiple('s3')}
          {opensTaxonomy(value) && single('s4')}
          {opensSfdr(value) && single('s5')}
          {opensAdverseImpact(value) && multiple('s6')}
        </>
      )}
    </Box>
  );
};
